// src/utils/unit.ts
var COMMA = ",";
var PERCENT_SIGN = "%";
function toNumber(text) {
  const num = parseInt(text, 10);
  return !Number.isNaN(num) ? num : null;
}
function toPercentage(text) {
  const num = parseInt(text.replace(PERCENT_SIGN, ""), 10);
  return !Number.isNaN(num) && num >= 0 && num <= 100 ? num : null;
}
function toCoords(text) {
  if (!text.includes(COMMA))
    return null;
  const [x, y] = text.split(COMMA).map(toPercentage);
  return x !== null && y !== null ? [x, y] : null;
}
function toFloat(text) {
  const num = parseFloat(text);
  return !Number.isNaN(num) ? num : null;
}

// src/vtt/text-cue.ts
var TextCue = class extends EventTarget {
  constructor(startTime, endTime, text) {
    super();
    /**
     * A string that identifies the cue.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/TextTrackCue/id}
     */
    this.id = "";
    /**
     * A `boolean` for whether the video will pause when this cue stops being displayed.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/TextTrackCue/pauseOnExit}
     */
    this.pauseOnExit = false;
    this.startTime = startTime;
    this.endTime = endTime;
    this.text = text;
  }
  addEventListener(type, listener, options) {
    super.addEventListener(type, listener, options);
  }
  removeEventListener(type, listener, options) {
    super.removeEventListener(type, listener, options);
  }
};

// src/vtt/vtt-cue.ts
var CueBase = false ? TextCue : window.VTTCue;
var VTTCue = class extends CueBase {
  constructor() {
    super(...arguments);
    /**
     * A `VTTRegion` object describing the video's sub-region that the cue will be drawn onto,
     * or `null` if none is assigned.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/region}
     */
    this.region = null;
    /**
     * The cue writing direction.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/vertical}
     */
    this.vertical = "";
    /**
     * Returns `true` if the `VTTCue.line` attribute is an integer number of lines or a percentage
     * of the video size.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/snapToLines}
     */
    this.snapToLines = true;
    /**
     * Returns the line positioning of the cue. This can be the string `'auto'` or a number whose
     * interpretation depends on the value of `VTTCue.snapToLines`.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/line}
     */
    this.line = "auto";
    /**
     * Returns an enum representing the alignment of the `VTTCue.line`.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/lineAlign}
     */
    this.lineAlign = "start";
    /**
     * Returns the indentation of the cue within the line. This can be the string `'auto'` or a
     * number representing the percentage of the `VTTCue.region`, or the video size if `VTTCue`.region`
     * is `null`.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/position}
     */
    this.position = "auto";
    /**
     * Returns an enum representing the alignment of the cue. This is used to determine what
     * the `VTTCue.position` is anchored to. The default is `'auto'`.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/positionAlign}
     */
    this.positionAlign = "auto";
    /**
     * Returns a double representing the size of the cue, as a percentage of the video size.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/size}
     */
    this.size = 100;
    /**
     * Returns an enum representing the alignment of all the lines of text within the cue box.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/VTTCue/align}
     */
    this.align = "center";
  }
};

// src/vtt/vtt-region.ts
var VTTRegion = class {
  constructor() {
    /**
     * A string that identifies the region.
     */
    this.id = "";
    /**
     * A `double` representing the width of the region, as a percentage of the video.
     */
    this.width = 100;
    /**
     * A `double` representing the height of the region, in number of lines.
     */
    this.lines = 3;
    /**
     * A `double` representing the region anchor X offset, as a percentage of the region.
     */
    this.regionAnchorX = 0;
    /**
     * A `double` representing the region anchor Y offset, as a percentage of the region.
     */
    this.regionAnchorY = 100;
    /**
     * A `double` representing the viewport anchor X offset, as a percentage of the video.
     */
    this.viewportAnchorX = 0;
    /**
     * A `double` representing the viewport anchor Y offset, as a percentage of the video.
     */
    this.viewportAnchorY = 100;
    /**
     * An enum representing how adding new cues will move existing cues.
     */
    this.scroll = "";
  }
};

// src/vtt/parse.ts
var HEADER_MAGIC = "WEBVTT";
var COMMA2 = ",";
var PERCENT_SIGN2 = "%";
var SETTING_SEP_RE = /[:=]/;
var SETTING_LINE_RE = /^[\s\t]*(region|vertical|line|position|size|align)[:=]/;
var NOTE_BLOCK_START = "NOTE";
var REGION_BLOCK_START = "REGION";
var REGION_BLOCK_START_RE = /^REGION:?[\s\t]+/;
var SPACE_RE = /[\s\t]+/;
var TIMESTAMP_SEP = "-->";
var TIMESTAMP_SEP_RE = /[\s\t]*-->[\s\t]+/;
var ALIGN_RE = /start|center|end|left|right/;
var LINE_ALIGN_RE = /start|center|end/;
var POS_ALIGN_RE = /line-(?:left|right)|center|auto/;
var TIMESTAMP_RE = /^(?:(\d{1,2}):)?(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/;
var VTTBlock = /* @__PURE__ */ ((VTTBlock2) => {
  VTTBlock2[VTTBlock2["None"] = 0] = "None";
  VTTBlock2[VTTBlock2["Header"] = 1] = "Header";
  VTTBlock2[VTTBlock2["Cue"] = 2] = "Cue";
  VTTBlock2[VTTBlock2["Region"] = 3] = "Region";
  VTTBlock2[VTTBlock2["Note"] = 4] = "Note";
  return VTTBlock2;
})(VTTBlock || {});
var VTTParser = class {
  constructor() {
    this.k = 0 /* None */;
    this.x = {};
    this.l = {};
    this.d = [];
    this.a = null;
    this.g = null;
    this.p = [];
    this.t = "";
  }
  async init(init) {
    this.f = init;
    if (init.strict)
      this.k = 1 /* Header */;
    if (init.errors)
      this.e = (await import("./errors-7ZCJBJFL.js")).ParseErrorBuilder;
  }
  parse(line, lineCount) {
    if (line === "") {
      if (this.a) {
        this.d.push(this.a);
        this.f.onCue?.(this.a);
        this.a = null;
      } else if (this.g) {
        this.l[this.g.id] = this.g;
        this.f.onRegion?.(this.g);
        this.g = null;
      } else if (this.k === 1 /* Header */) {
        this.y(line, lineCount);
        this.f.onHeaderMetadata?.(this.x);
      }
      this.k = 0 /* None */;
    } else if (this.k) {
      switch (this.k) {
        case 1 /* Header */:
          this.y(line, lineCount);
          break;
        case 2 /* Cue */:
          if (this.a) {
            const hasText = this.a.text.length > 0;
            if (!hasText && SETTING_LINE_RE.test(line)) {
              this.I(line.split(SPACE_RE), lineCount);
            } else {
              this.a.text += (hasText ? "\n" : "") + line;
            }
          }
          break;
        case 3 /* Region */:
          this.J(line.split(SPACE_RE), lineCount);
          break;
      }
    } else if (line.startsWith(NOTE_BLOCK_START)) {
      this.k = 4 /* Note */;
    } else if (line.startsWith(REGION_BLOCK_START)) {
      this.k = 3 /* Region */;
      this.g = new VTTRegion();
      this.J(line.replace(REGION_BLOCK_START_RE, "").split(SPACE_RE), lineCount);
    } else if (line.includes(TIMESTAMP_SEP)) {
      const result = this.o(line, lineCount);
      if (result) {
        this.a = new VTTCue(result[0], result[1], "");
        this.a.id = this.t;
        this.I(result[2], lineCount);
      }
      this.k = 2 /* Cue */;
    } else if (lineCount === 1) {
      this.y(line, lineCount);
    }
    this.t = line;
  }
  done() {
    return {
      metadata: this.x,
      cues: this.d,
      regions: Object.values(this.l),
      errors: this.p
    };
  }
  y(line, lineCount) {
    if (lineCount > 1) {
      if (SETTING_SEP_RE.test(line)) {
        const [key, value] = line.split(SETTING_SEP_RE);
        if (key)
          this.x[key] = (value || "").replace(SPACE_RE, "");
      }
    } else if (line.startsWith(HEADER_MAGIC)) {
      this.k = 1 /* Header */;
    } else {
      this.h(this.e?.P());
    }
  }
  o(line, lineCount) {
    const [startTimeText, trailingText = ""] = line.split(TIMESTAMP_SEP_RE), [endTimeText, ...settingsText] = trailingText.split(SPACE_RE), startTime = parseVTTTimestamp(startTimeText), endTime = parseVTTTimestamp(endTimeText);
    if (startTime !== null && endTime !== null && endTime > startTime) {
      return [startTime, endTime, settingsText];
    } else {
      if (startTime === null) {
        this.h(this.e?.E(startTimeText, lineCount));
      }
      if (endTime === null) {
        this.h(this.e?.F(endTimeText, lineCount));
      }
      if (startTime != null && endTime !== null && endTime > startTime) {
        this.h(this.e?.G(startTime, endTime, lineCount));
      }
    }
  }
  /**
   * @see {@link https://www.w3.org/TR/webvtt1/#region-settings-parsing}
   */
  J(settings, line) {
    let badValue;
    for (let i = 0; i < settings.length; i++) {
      if (SETTING_SEP_RE.test(settings[i])) {
        badValue = false;
        const [name, value] = settings[i].split(SETTING_SEP_RE);
        switch (name) {
          case "id":
            this.g.id = value;
            break;
          case "width":
            const width = toPercentage(value);
            if (width !== null)
              this.g.width = width;
            else
              badValue = true;
            break;
          case "lines":
            const lines = toNumber(value);
            if (lines !== null)
              this.g.lines = lines;
            else
              badValue = true;
            break;
          case "regionanchor":
            const region = toCoords(value);
            if (region !== null) {
              this.g.regionAnchorX = region[0];
              this.g.regionAnchorY = region[1];
            } else
              badValue = true;
            break;
          case "viewportanchor":
            const viewport = toCoords(value);
            if (viewport !== null) {
              this.g.viewportAnchorX = viewport[0];
              this.g.viewportAnchorY = viewport[1];
            } else
              badValue = true;
            break;
          case "scroll":
            if (value === "up")
              this.g.scroll = "up";
            else
              badValue = true;
            break;
          default:
            this.h(this.e?.T(name, value, line));
        }
        if (badValue) {
          this.h(this.e?.S(name, value, line));
        }
      }
    }
  }
  /**
   * @see {@link https://www.w3.org/TR/webvtt1/#cue-timings-and-settings-parsing}
   */
  I(settings, line) {
    let badValue;
    for (let i = 0; i < settings.length; i++) {
      badValue = false;
      if (SETTING_SEP_RE.test(settings[i])) {
        const [name, value] = settings[i].split(SETTING_SEP_RE);
        switch (name) {
          case "region":
            const region = this.l[value];
            if (region)
              this.a.region = region;
            break;
          case "vertical":
            if (value === "lr" || value === "rl") {
              this.a.vertical = value;
              this.a.region = null;
            } else
              badValue = true;
            break;
          case "line":
            const [linePos, lineAlign] = value.split(COMMA2);
            if (linePos.includes(PERCENT_SIGN2)) {
              const percentage = toPercentage(linePos);
              if (percentage !== null) {
                this.a.line = percentage;
                this.a.snapToLines = false;
              } else
                badValue = true;
            } else {
              const number = toFloat(linePos);
              if (number !== null)
                this.a.line = number;
              else
                badValue = true;
            }
            if (LINE_ALIGN_RE.test(lineAlign)) {
              this.a.lineAlign = lineAlign;
            } else if (lineAlign) {
              badValue = true;
            }
            if (this.a.line !== "auto")
              this.a.region = null;
            break;
          case "position":
            const [colPos, colAlign] = value.split(COMMA2), position = toPercentage(colPos);
            if (position !== null)
              this.a.position = position;
            else
              badValue = true;
            if (colAlign && POS_ALIGN_RE.test(colAlign)) {
              this.a.positionAlign = colAlign;
            } else if (colAlign) {
              badValue = true;
            }
            break;
          case "size":
            const size = toPercentage(value);
            if (size !== null) {
              this.a.size = size;
              if (size < 100)
                this.a.region = null;
            } else {
              badValue = true;
            }
            break;
          case "align":
            if (ALIGN_RE.test(value)) {
              this.a.align = value;
            } else {
              badValue = true;
            }
            break;
          default:
            this.h(this.e?.R(name, value, line));
        }
        if (badValue) {
          this.h(this.e?.Q(name, value, line));
        }
      }
    }
  }
  h(error) {
    if (!error)
      return;
    this.p.push(error);
    if (this.f.strict) {
      this.f.cancel();
      throw error;
    } else {
      this.f.onError?.(error);
    }
  }
};
function parseVTTTimestamp(timestamp) {
  const match = timestamp.match(TIMESTAMP_RE);
  if (!match)
    return null;
  const hours = match[1] ? parseInt(match[1], 10) : 0, minutes = parseInt(match[2], 10), seconds = parseInt(match[3], 10), milliseconds = match[4] ? parseInt(match[4].padEnd(3, "0"), 10) : 0, total = hours * 3600 + minutes * 60 + seconds + milliseconds / 1e3;
  if (hours < 0 || minutes < 0 || seconds < 0 || milliseconds < 0 || minutes > 59 || seconds > 59) {
    return null;
  }
  return total;
}
function createVTTParser() {
  return new VTTParser();
}

export {
  TextCue,
  VTTCue,
  VTTRegion,
  VTTBlock,
  VTTParser,
  parseVTTTimestamp,
  createVTTParser
};
